import { motion } from "framer-motion";
import { useState } from "react";
import "../style/ContactStyle.css";

const ContactSection = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');

    const fadeInUp = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0 }
    };

  
    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
        if (submitStatus === 'error') {
            setSubmitStatus(null);
            setErrorMessage('');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus(null);
        setErrorMessage('');

        try {
            // Direct call to Firebase Cloud Function
            const response = await fetch('https://us-central1-daydone-83ae0.cloudfunctions.net/submitContactForm', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    data: {
                        name: formData.name,
                        email: formData.email,
                        message: formData.message
                    }
                })
            });

            if (!response.ok) {
                throw new Error('Failed to send message');
            }

            const result = await response.json();

            if (result.result && result.result.success) {
                setSubmitStatus('success');
                setFormData({ name: '', email: '', message: '' });
                setTimeout(() => setSubmitStatus(null), 5000);
            } else {
                throw new Error(result.result?.message || 'Failed to send message');
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            setSubmitStatus('error');
            setErrorMessage('Failed to send message. Please try again or email us directly at enquiries@daydone.co.uk');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section className="contact-section">
            <div className="container">
                <motion.div
                    className="text-center mb-5"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={{
                        visible: {
                            transition: {
                                staggerChildren: 0.15
                            }
                        }
                    }}
                >
                    <motion.h2
                        className="contact-title"
                        variants={fadeInUp}
                    >
                        Get in Touch
                    </motion.h2>
                    <motion.p
                        className="contact-subtitle"
                        variants={fadeInUp}
                    >
                        Have questions? We're here to help. Reach out to our UK-based support team.
                    </motion.p>
                </motion.div>

            

                {/* Contact Form */}
                <motion.div
                    className="contact-form-wrapper"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.4 }}
                >
                    <div className="contact-form-container">
                        <h3 className="contact-form-title">Send Us a Message</h3>
                        <p className="contact-form-subtitle">
                            Fill out the form below and we'll get back to you as soon as possible.
                        </p>
                        
                        <form onSubmit={handleSubmit} className="contact-form">
                            <div className="form-group">
                                <label htmlFor="name" className="form-label">Name</label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    className="form-input"
                                    placeholder="Your name"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="email" className="form-label">Email</label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className="form-input"
                                    placeholder="your@email.com"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="message" className="form-label">Message</label>
                                <textarea
                                    id="message"
                                    name="message"
                                    value={formData.message}
                                    onChange={handleChange}
                                    className="form-textarea"
                                    placeholder="Tell us how we can help..."
                                    rows="6"
                                    required
                                    minLength={10}
                                    maxLength={5000}
                                ></textarea>
                            </div>

                            <button 
                                type="submit" 
                                className="form-submit-button"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Sending...' : 'Send Message'}
                            </button>

                            {submitStatus === 'success' && (
                                <div className="form-success-message">
                                    Thank you! Your message has been sent successfully. We'll get back to you soon.
                                </div>
                            )}

                            {submitStatus === 'error' && (
                                <div className="form-error-message">
                                    {errorMessage}
                                </div>
                            )}
                        </form>
                    </div>
                </motion.div>

                <motion.div
                    className="contact-cta"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.6 }}
                >
                    <div className="contact-cta-content">
                        <h3 className="contact-cta-title">Ready to Get Started?</h3>
                        <p className="contact-cta-text">
                            Start your free trial today and see how DayDone can transform your business.
                        </p>
                        <button 
                            className="contact-cta-button"
                            onClick={() => window.location.href = 'https://web.daydone.co.uk'}
                        >
                            Start Free Trial
                        </button>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default ContactSection;